/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetIdentityProviderConnectionRequest,
  GetIdentityProviderConnectionSecurity,
} from "../models/operations/getidentityproviderconnection.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildIdentityProviderConnectionQuery,
  IdentityProviderConnectionQueryData,
  prefetchIdentityProviderConnection,
  queryKeyIdentityProviderConnection,
} from "./identityProviderConnection.core.js";
export {
  buildIdentityProviderConnectionQuery,
  type IdentityProviderConnectionQueryData,
  prefetchIdentityProviderConnection,
  queryKeyIdentityProviderConnection,
};

export type IdentityProviderConnectionQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * get identityProviderConnections
 *
 * @remarks
 * Get a connection by ID, or the organization's live Okta connection when no ID is given. Session only; requires org:read.
 */
export function useIdentityProviderConnection(
  request?: GetIdentityProviderConnectionRequest | undefined,
  security?: GetIdentityProviderConnectionSecurity | undefined,
  options?: QueryHookOptions<
    IdentityProviderConnectionQueryData,
    IdentityProviderConnectionQueryError
  >,
): UseQueryResult<
  IdentityProviderConnectionQueryData,
  IdentityProviderConnectionQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildIdentityProviderConnectionQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * get identityProviderConnections
 *
 * @remarks
 * Get a connection by ID, or the organization's live Okta connection when no ID is given. Session only; requires org:read.
 */
export function useIdentityProviderConnectionSuspense(
  request?: GetIdentityProviderConnectionRequest | undefined,
  security?: GetIdentityProviderConnectionSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    IdentityProviderConnectionQueryData,
    IdentityProviderConnectionQueryError
  >,
): UseSuspenseQueryResult<
  IdentityProviderConnectionQueryData,
  IdentityProviderConnectionQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildIdentityProviderConnectionQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setIdentityProviderConnectionData(
  client: QueryClient,
  queryKeyBase: [
    parameters: { id?: string | undefined; gramSession?: string | undefined },
  ],
  data: IdentityProviderConnectionQueryData,
): IdentityProviderConnectionQueryData | undefined {
  const key = queryKeyIdentityProviderConnection(...queryKeyBase);

  return client.setQueryData<IdentityProviderConnectionQueryData>(key, data);
}

export function invalidateIdentityProviderConnection(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { id?: string | undefined; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "identityProviderConnections",
      "get",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllIdentityProviderConnection(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "identityProviderConnections", "get"],
  });
}
